import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { AUTO_RETRY_DELAYS_MS, aiFailure } from '../../utils/aiIdentify';
import { can } from '../../utils/permissions';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import Button from '../ui/Button';

/**
 * A field answer beside DNA-sequenced references (L17b; docs/LIVE_UX_PLAN.md,
 * routes/lab_scores.py, routes/dna_validation.py).
 *
 * A person presses for it, since it wakes the model. Each candidate shows, in the
 * answer's own order, its closest reference whose DNA and vetted name agree, or
 * that it has none, which proves nothing. It holds no accept and moves nothing:
 * the matches above it, their percentages and the warnings stay as they are.
 *
 * Lab and Final read the score contract's empty states, since the gate hasn't
 * passed (L17a; LAB_AI_SCHEMA_0111.md 7.1). The number beside a field score appears
 * only when the server has a calibration that holds up, which it doesn't yet.
 *
 * Shown only to those holding reference:search, for an identification with an id.
 */
export default function DnaReferenceCheck({ aiId, user, requestConfig, testId = 'dna-check' }) {
  const { t } = useTranslation('ai');
  const [phase, setPhase] = useState('idle');
  const [answer, setAnswer] = useState(null);
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const timer = useRef(null);
  const asked = useRef(0);

  // Another identification: whatever was said was about the last one. Not on mounting, as in ReferenceCompare.
  const shown = useRef(aiId);
  useEffect(() => {
    if (shown.current === aiId) return;
    shown.current = aiId;
    asked.current += 1;
    clearTimeout(timer.current);
    setPhase('idle');
    setAnswer(null);
    setFailure(null);
  }, [aiId]);
  useEffect(() => () => {
    asked.current += 1;
    clearTimeout(timer.current);
  }, []);

  if (!aiId || !can(user, 'reference:search')) return null;

  const ask = async (tries = 0) => {
    clearTimeout(timer.current);
    const mine = ++asked.current;
    setFailure(null);
    setPhase('running');
    setAttempt(tries);
    const body = { ai_id: aiId };
    try {
      const { data } = await (requestConfig
        ? api.post('/references/dna-check', body, requestConfig)
        : api.post('/references/dna-check', body));
      if (mine !== asked.current) return;
      setAnswer(data);
      setPhase('done');
    } catch (e) {
      if (mine !== asked.current) return;
      const found = aiFailure(e);
      if (found.busy && tries < AUTO_RETRY_DELAYS_MS.length) {
        timer.current = setTimeout(() => ask(tries + 1), AUTO_RETRY_DELAYS_MS[tries]);
        setAttempt(tries + 1);
        return;
      }
      setFailure(found);
      setPhase('failed');
    }
  };

  const failureText = failure?.code === 'embeddings_not_served' ? t('references.notServed') : t(failure?.key || 'failed');
  const leftOut = (answer?.left_out?.disagrees || 0) + (answer?.left_out?.markers_conflict || 0);

  return (
    <section style={st.box} aria-label={t('dnaCheck.title')} data-testid={testId}>
      {phase === 'idle' && (
        <Button onClick={() => ask()} title={t('tip.dnaCheckRun')} data-testid={`${testId}-run`}>
          {t('dnaCheck.run')}
        </Button>
      )}
      {phase === 'running' && (
        <p role="status" style={st.muted} data-testid={`${testId}-running`}>
          {attempt > 0 ? t('warming', { attempt, total: AUTO_RETRY_DELAYS_MS.length }) : t('dnaCheck.running')}
        </p>
      )}
      {phase === 'failed' && (
        <div role="alert" style={st.muted} data-testid={`${testId}-failed`}>
          <p style={{ margin: '0 0 6px' }}>{failureText}</p>
          <Button variant="ghost" onClick={() => ask()} title={t('tip.retry')} data-testid={`${testId}-retry`}>
            {t('retry')}
          </Button>
        </div>
      )}
      {phase === 'done' && (
        <>
          <h3 style={st.title}>{t('dnaCheck.title')}</h3>
          <ul style={st.columns} data-testid={`${testId}-columns`}>
            {answer.lab?.status !== 'ok' && <li>{t('dnaCheck.labNone')}</li>}
            {answer.final?.status !== 'ok' && <li>{t('dnaCheck.finalNone')}</li>}
          </ul>
          {answer.status === 'no_references' ? (
            <p style={st.muted} data-testid={`${testId}-empty`}>{t('dnaCheck.empty')}</p>
          ) : (
            <ol style={st.list} data-testid={`${testId}-list`}>
              {(answer.candidates || []).map((c) => (
                <Candidate key={c.taxon_name} c={c} t={t} testId={testId} />
              ))}
            </ol>
          )}
          {leftOut > 0 && <p style={st.note} data-testid={`${testId}-left-out`}>{t('dnaCheck.leftOut', { count: leftOut })}</p>}
          {answer.status !== 'ok' && answer.status !== 'no_references' && (
            <p style={st.note} data-testid={`${testId}-not-fitted`}>{t('dnaCheck.notFitted')}</p>
          )}
          <p style={st.note} data-testid={`${testId}-note`}>{t('dnaCheck.note')}</p>
        </>
      )}
    </section>
  );
}

function Candidate({ c, t, testId }) {
  const percent = (value) => Math.round(value * 100);
  return (
    <li style={st.candidate} data-testid={`${testId}-candidate`} data-taxon={c.taxon_name}>
      <bdi dir="ltr" style={{ fontStyle: 'italic', fontSize: 13 }}>{c.taxon_name}</bdi>
      {c.evidence !== 'compared' ? (
        <p style={st.muted} data-testid={`${testId}-none`}>{t('dnaCheck.noReference')}</p>
      ) : (
        <div style={st.compared}>
          {c.closest && (
            <a href={resolvePhotoUrl(c.closest.s3_url)} target="_blank" rel="noopener noreferrer" title={t('tip.referencesOpen')}>
              <img src={resolvePhotoUrl(c.closest.thumbnail_url || c.closest.s3_url)} loading="lazy"
                alt={t('dnaCheck.alt', { taxon: c.closest.taxon_name })} style={st.image} />
            </a>
          )}
          <div style={st.reading}>
            <span style={st.score} title={t('tip.referencesScore')}>{t('references.closeness', { score: c.cosine.toFixed(2) })}</span>
            <span style={st.level}>{t(c.compared_at === 'species' ? 'dnaCheck.atSpecies' : 'dnaCheck.atGenus')}</span>
            {c.closest?.record_code && <bdi dir="ltr" style={st.level}>{c.closest.record_code}</bdi>}
            {c.posterior != null && c.field_score != null && (
              <span style={st.level} data-testid={`${testId}-fitted`}>
                {t('dnaCheck.fitted', { field: percent(c.field_score), after: percent(c.posterior) })}
              </span>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

const st = {
  box: { marginTop: 10 },
  title: { margin: '0 0 6px', fontSize: 14, color: 'var(--c-text-body-fg)' },
  muted: { margin: 0, fontSize: 13, color: 'var(--c-text-muted-2)' },
  columns: { listStyle: 'none', margin: '0 0 8px', padding: 0, fontSize: 12, color: 'var(--c-text-muted-2)' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 },
  candidate: { minWidth: 0 },
  compared: { display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 4 },
  image: { width: 72, height: 72, objectFit: 'cover', borderRadius: 6, display: 'block', background: 'var(--c-surface)' },
  reading: { display: 'grid', gap: 2, minWidth: 0 },
  score: { fontSize: 12, color: 'var(--c-text-muted-2)', fontVariantNumeric: 'tabular-nums' },
  level: { fontSize: 12, color: 'var(--c-text-muted-2)', overflowWrap: 'anywhere' },
  note: { margin: '8px 0 0', fontSize: 12, color: 'var(--c-text-muted-2)' },
};
